import { useRef, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { COMPANY_STATUS_LABELS, isCompanySkipStatus } from "../companyStatus";
import { checkCompanyDomain, createPrimeCompany, upsertPrimeCompany, type PrimeCompany } from "../api/CrmApi";
import { apiErrorMessage } from "../apiError";
import DomainChipsInput, { withDomains } from "./DomainChipsInput";
import NaicsCodesInput from "./NaicsCodesInput";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  /** The company to edit. Leave it out to use the form as "Add company". */
  company?: PrimeCompany | null;
  onClose: () => void;
  onSaved?: (company: PrimeCompany) => void;
}

const LABEL = "block text-xs font-medium text-muted-foreground mb-1.5";
const INPUT = `w-full px-3 py-2 ${FORM_CONTROL}`;

const NAICS_FORMAT = /^[0-9]{2,6}$/;

export default function EditCompanyModal({ company, onClose, onSaved }: Props) {
  const creating = !company;
  const [naicsCodes, setNaicsCodes] = useState<string[]>([]);
  const [duplicate, setDuplicate] = useState<string | null>(null); // a same-name company exists: ask before adding another
  // What the server says about the Main Domain once the field is left: free (and how it would be saved) or taken.
  const [domainNote, setDomainNote] = useState<{ ok: boolean; text: string } | null>(null);
  const latestDomain = useRef("");
  const [name, setName] = useState(company?.name ?? "");
  const [domain, setDomain] = useState(company?.domain ?? "");
  const [sites, setSites] = useState<string[]>(company?.additional_domains ?? []);
  const [sitesDraft, setSitesDraft] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState(company?.linkedin_url ?? "");
  const [employeeRange, setEmployeeRange] = useState(company?.employee_range ?? "");
  const [revenue, setRevenue] = useState(company?.annual_revenue === null || company?.annual_revenue === undefined ? "" : String(company.annual_revenue));
  const [revenueSource, setRevenueSource] = useState(company?.revenue_source ?? "");
  const [nigp, setNigp] = useState((company?.nigp_codes ?? []).join(", "));
  const [orgType, setOrgType] = useState(company?.org_type ?? "");
  const [status, setStatus] = useState(company?.status || "active");
  const skipStatus = isCompanySkipStatus(company?.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function checkDomain() {
    const typed = domain.trim();
    latestDomain.current = typed;
    // nothing to say about an empty field, or about an edit that left the company's own domain as it was
    if (!typed || (!creating && typed === (company?.domain ?? ""))) return setDomainNote(null);
    try {
      const verdict = await checkCompanyDomain(typed, company?.id);
      if (latestDomain.current !== typed) return; // the field changed while the answer was on its way
      if (!verdict.valid || verdict.owner) setDomainNote({ ok: false, text: verdict.message || "This domain cannot be used." });
      else setDomainNote({ ok: true, text: verdict.domain && verdict.domain !== typed ? `Available — it will be saved as ${verdict.domain}.` : "Available." });
    } catch {
      setDomainNote(null); // the save still checks it
    }
  }

  async function create(allowDuplicateName = false) {
    if (!name.trim()) return setError("Enter the company name.");
    if (!domain.trim()) return setError("Enter the company's main domain, for example acme.com.");
    if (naicsCodes.length === 0) return setError("Add at least one NAICS code.");
    const bad = naicsCodes.filter((c) => !NAICS_FORMAT.test(c));
    if (bad.length > 0) return setError(`NAICS codes must be 2 to 6 digits: ${bad.join(", ")}`);
    if (!naicsCodes.some((c) => c.length >= 4)) return setError("Add at least one NAICS code of 4 or more digits — shorter codes are ignored when a company is matched to an RFP.");
    setSaving(true);
    setError(null);
    setDuplicate(null);
    try {
      const created = await createPrimeCompany({
        name: name.trim(), domain: domain.trim(), naics_codes: naicsCodes,
        ...(linkedinUrl.trim() ? { linkedin_url: linkedinUrl.trim() } : {}),
        ...(orgType.trim() ? { org_type: orgType.trim() } : {}),
        ...(employeeRange.trim() ? { employee_range: employeeRange.trim() } : {}),
        ...(allowDuplicateName ? { allow_duplicate_name: true } : {}),
      });
      setSaved(true);
      onSaved?.(created);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_name") {
        setDuplicate(detail.message || "A company with this name already exists.");
      } else {
        setError(apiErrorMessage(err, "Failed to add the company. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  }

  async function confirm() {
    if (creating) return create();
    if (!company) return;
    const revenueText = revenue.replace(/[$,\s]/g, "");
    if (revenueText !== "" && !/^\d+$/.test(revenueText)) {
      setError("Annual revenue must be a whole number of dollars (for example 5600000).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Whatever is typed in the Other Websites box but not yet turned into a chip counts too.
      const finalSites = withDomains(sites, sitesDraft);
      const updated = await upsertPrimeCompany({
        company_id: company.id,
        name: name.trim(),
        // Only sent when changed, so an untouched domain is never rewritten.
        ...(domain.trim() !== (company.domain ?? "") ? { domain: domain.trim() } : {}),
        // Only sent when changed; the server cleans, dedupes and checks uniqueness.
        ...(JSON.stringify(finalSites) !== JSON.stringify(company.additional_domains ?? []) ? { additional_domains: finalSites } : {}),
        linkedin_url: linkedinUrl.trim(),
        employee_range: employeeRange.trim() || null,
        annual_revenue: revenueText === "" ? null : Number(revenueText),
        revenue_source: revenueSource.trim() || null,
        nigp_codes: nigp.split(/[,;\s]+/).map((c) => c.trim()).filter(Boolean),
        org_type: orgType.trim() || null,
        ...(skipStatus ? {} : { status }),
      });
      onSaved?.(updated);
      setSaved(true);
      setTimeout(onClose, 600);
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to save. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title={creating ? "Add Company" : "Edit Company"}
      description={creating ? "Added by hand — name, main domain and at least one NAICS code are required" : (company?.domain || "no resolved domain")}
      icon={creating ? <Plus /> : <Pencil />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} disabled={saving || saved}>
            {saving ? (creating ? "Adding…" : "Saving…") : saved ? (creating ? "Added" : "Saved") : (creating ? "Add company" : "Save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className={LABEL}>{creating ? "Name *" : "Name"}</label>
          <input type="text" aria-label="Company name" value={name} onChange={(e) => { setName(e.target.value); setDuplicate(null); }} className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>{creating ? "Main Domain *" : "Main Domain"}</label>
          <input
            type="text"
            aria-label="Main domain"
            value={domain}
            onChange={(e) => { setDomain(e.target.value); setDomainNote(null); setDuplicate(null); latestDomain.current = ""; }}
            onBlur={checkDomain}
            placeholder="acme.com"
            className={INPUT}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            The company's main domain — usually the one its contacts' email addresses use. Contacts on it are linked to this company. Must be unique.
          </p>
          <div className="min-h-[2.25rem]">
            {domainNote && (
              <p role="status" className={`text-[11px] mt-1 font-medium ${domainNote.ok ? "text-success-soft-foreground" : "text-destructive"}`}>
                {domainNote.text}
              </p>
            )}
          </div>
        </div>
        {creating && (
          <div>
            <label className={LABEL}>NAICS codes *</label>
            <NaicsCodesInput codes={naicsCodes} onChange={setNaicsCodes} disabled={saving} />
            <p className="text-[11px] text-muted-foreground mt-1">
              At least one code of 4 to 6 digits. They are what an RFP's industry is matched against; a 2 or 3 digit sector code can be added too but is ignored for matching.
            </p>
          </div>
        )}
        {!creating && (
        <div>
          <label className={LABEL}>Other Websites</label>
          <DomainChipsInput
            value={sites}
            onChange={setSites}
            draft={sitesDraft}
            onDraftChange={setSitesDraft}
            disabled={saving}
            placeholder="add a website, e.g. acme-corp.com"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Other websites this company uses (its own site, for example). Shown as links; a contact whose email is on one of them is linked to this company too.
          </p>
        </div>
        )}
        <div>
          <label className={LABEL}>LinkedIn URL</label>
          <input
            type="text"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="https://www.linkedin.com/company/..."
            className={INPUT}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Size</label>
            <input type="text" value={employeeRange} onChange={(e) => setEmployeeRange(e.target.value)} placeholder="10-50" className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Organization type</label>
            <input type="text" value={orgType} onChange={(e) => setOrgType(e.target.value)} placeholder="For-Profit" className={INPUT} />
          </div>
          {!creating && (<>
          <div>
            <label className={LABEL}>Annual revenue (USD)</label>
            <input type="text" inputMode="numeric" value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder="5600000" className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Revenue source</label>
            <input type="text" value={revenueSource} onChange={(e) => setRevenueSource(e.target.value)} placeholder="researched" className={INPUT} />
          </div>
          </>)}
        </div>
        {!creating && (<>
        <div>
          <label className={LABEL}>NIGP codes</label>
          <input type="text" value={nigp} onChange={(e) => setNigp(e.target.value)} placeholder="91394, 91375" className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT} disabled={skipStatus}>
            <option value="active">active</option>
            <option value="do_not_contact">do_not_contact</option>
            {skipStatus && <option value={status}>{COMPANY_STATUS_LABELS[status]}</option>}
          </select>
          {skipStatus && <p className="text-[11px] text-muted-foreground mt-1">Set with a reason; use Re-open on the company page to change it.</p>}
        </div>
        </>)}
      </div>

      {duplicate && (
        <Alert tone="warning" className="mt-4" role="alert">
          <p>{duplicate}</p>
          <p className="mt-1">Add this one as well only if it is a different company that happens to share the name.</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => create(true)} disabled={saving}>
            Add anyway
          </Button>
        </Alert>
      )}

      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
          {error.toLowerCase().includes("naics_codes") && " — tag at least one NAICS code below first, then this edit can be saved."}
        </Alert>
      )}
    </Modal>
  );
}
